import { useQuery } from '@tanstack/react-query';
import type { TypedDocumentNode } from '@graphql-typed-document-node/core';
import type { Variables } from 'graphql-request';
import type { UseQueryResult, UseQueryOptions } from '@tanstack/react-query';

import { defaultClient } from '@e-socna/graphql-clients';
import { getDocumentHash, getDocumentName } from '@e-socna/data-layer';
import { EMPTY_ARRAY } from '@e-socna/empty';

import { environment } from '../environment';
import { checkIfIsTransactionConflictError } from '../utils/check-if-is-transaction-conflict-error.util';
import {
  PersistedQueryNotFoundError,
  sendPersistedQuery,
} from '../utils/send-persisted-query.util';

export type QueryOptions<TResult, TError extends Error = Error> = Omit<
  UseQueryOptions<TResult, TError, TResult>,
  'queryKey'
>;

export interface GenerateBasicQueryOptionsParams<
  TResult,
  TVariables extends Variables | undefined,
  TError extends Error = Error,
> {
  readonly document: TypedDocumentNode<TResult, TVariables>;
  readonly variables?: TVariables;
  readonly useQueryOptions?: QueryOptions<TResult, TError>;
}

export const generateBasicQueryOptions = <
  TResult,
  TVariables extends Variables | undefined,
  TError extends Error = Error,
>({
  document,
  variables,
  useQueryOptions,
}: GenerateBasicQueryOptionsParams<
  TResult,
  TVariables,
  TError
>): UseQueryOptions<TResult, TError, TResult> => ({
  queryKey: [
    getDocumentName(document),
    ...(variables ? [variables] : EMPTY_ARRAY),
  ] as const,
  // The signal aborts a query no longer observed (its variables changed, its component left).
  queryFn: async ({ queryKey, signal }) => {
    const variables = queryKey[1] ? (queryKey[1] as TVariables) : undefined;
    const hash = environment.enablePersistedQueries
      ? getDocumentHash(document)
      : undefined;

    if (hash) {
      try {
        return await sendPersistedQuery<TResult, TVariables>(hash, variables, {
          endpoint: `${environment.backendUrl}${environment.graphQLEndpoint}`,
          signal,
        });
      } catch (error) {
        if (!(error instanceof PersistedQueryNotFoundError)) {
          throw error;
        }
      }
    }

    return defaultClient.request({ document, variables, signal });
  },
  retry: (failureCount, error) =>
    failureCount < environment.transactionConflictMaxRetry &&
    checkIfIsTransactionConflictError(error),
  ...useQueryOptions,
});

export function useGraphQLBasicQuery<
  TResult,
  TVariables extends Variables | undefined,
  TError extends Error = Error,
>(
  document: TypedDocumentNode<TResult, TVariables>,
  variables?: TVariables,
  useQueryOptions?: QueryOptions<TResult, TError>,
): UseQueryResult<TResult> {
  const queryParameters = generateBasicQueryOptions({
    document,
    variables,
    useQueryOptions,
  });

  return useQuery(queryParameters);
}
